import { FirebaseError } from "firebase/app";
import isFirebaseError from "./FirebaseError";

// Get current environment and Functions base URL
const getCurrentEnvironment = (): 'development' | 'production' => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname.includes('bbtech-dev-admin') || hostname.includes('localhost')) {
      return 'development';
    }
  }
  return 'production';
};

const getFunctionsBaseUrl = (): string => {
  const environment = getCurrentEnvironment();
  const baseUrl = environment === 'development' 
    ? 'https://asia-northeast3-bbtech-dev.cloudfunctions.net'
    : 'https://asia-northeast3-bbtech-b67fa.cloudfunctions.net';
  
  console.log(`Using Functions environment: ${environment}, URL: ${baseUrl}`);
  return baseUrl;
};

const verifyIdToken = async (token: string): Promise<boolean> => {
    try {
        const response = await fetch(
            `${getFunctionsBaseUrl()}/verifyIdToken`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
            },
              body: JSON.stringify({ token }),
            }
          );
        
        const data = await response.json();

        if (!response.ok) {
          const maybeError = data.error;
          // FirebaseError 객체로 throw
          if (isFirebaseError(maybeError)) {
            const firebaseError: FirebaseError = {
              code: maybeError.code,
              name: maybeError.name,
              message: maybeError.message
            };
    
            throw firebaseError;
          }
    
          // 그 외 일반 에러로 처리
          throw new Error(data.error || "Unknown error");
        }

        console.log(data);
        return data.valid;
    } catch (error) {
        console.error("[verifyIdToken] error: " + error);
        throw error;
    }
}

const sendAuthenticationMail = async (to: string): Promise<string> => {
  try {
      const response = await fetch(
          `${getFunctionsBaseUrl()}/sendAuthenticationEmail`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
          },
            body: JSON.stringify({
              to: to,
            }),
          }
        );
      
      const data = await response.json();

      if (!response.ok) {
        const maybeError = data.error;
        // FirebaseError 객체로 throw
        if (isFirebaseError(maybeError)) {
          const firebaseError: FirebaseError = {
            code: maybeError.code,
            name: maybeError.name,
            message: maybeError.message
          };
  
          throw firebaseError;
        }
  
        // 그 외 일반 에러로 처리
        throw new Error(data.message || "Unknown error");
      }

      console.log(data);
      return data.message;
  } catch (error) {
      console.error("[sendAuthenticationMail] error: " + error);
      throw error;
  }
}

const sendFindUsernameEmail = async (email: string, username: string): Promise<string> => {
  try {
      const response = await fetch(
          `${getFunctionsBaseUrl()}/sendFindUsernameEmail`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
          },
            body: JSON.stringify({
              email: email,
              username: username
            }),
          }
        );
      
      const data = await response.json();

      if (!response.ok) {
        const maybeError = data.error;
        // FirebaseError 객체로 throw
        if (isFirebaseError(maybeError)) {
          const firebaseError: FirebaseError = {
            code: maybeError.code,
            name: maybeError.name,
            message: maybeError.message
          };
  
          throw firebaseError;
        }
  
        // 그 외 일반 에러로 처리
        throw new Error(data.message || "Unknown error");
      }

      console.log(data);
      return data.message;
  } catch (error) {
      console.error("[sendFindUsernameEmail] error: " + error);
      throw error;
  }
}

const sendInquiryEmail = async (to: string, subject: string, text: string, html: string): Promise<string> => {
  try {
      const response = await fetch(
          `${getFunctionsBaseUrl()}/sendInquiryEMail`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
          },
            body: JSON.stringify({
              to: to,
              subject: subject,
              text: text,
              html: html,
            }),
          }
        );
      
      const data = await response.json();

      if (!response.ok) {
        const maybeError = data.error;
        // FirebaseError 객체로 throw
        if (isFirebaseError(maybeError)) {
          const firebaseError: FirebaseError = {
            code: maybeError.code,
            name: maybeError.name,
            message: maybeError.message
          };
  
          throw firebaseError;
        }
  
        // 그 외 일반 에러로 처리
        throw new Error(data.message || "Unknown error");
      }

      console.log(data);
      return data.message;
  } catch (error) {
      console.error("[sendInquiryEmail] error: " + error);
      throw error;
  }
}

const resetPassword = async (email: string, password: string, verificationId: string): Promise<string> => {
  try {
    const response = await fetch(
        `${getFunctionsBaseUrl()}/resetPassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
        },
          body: JSON.stringify({
            email: email,
            password: password,
            verificationId: verificationId
          }),
        }
      );
    
    const data = await response.json();

    if (!response.ok) {
      const maybeError = data.error;
      // FirebaseError 객체로 throw
      if (isFirebaseError(maybeError)) {
        const firebaseError: FirebaseError = {
          code: maybeError.code,
          name: maybeError.name,
          message: maybeError.message
        };

        throw firebaseError;
      }

      // 그 외 일반 에러로 처리
      throw new Error(data.message || "Unknown error");
    }

    console.log(data);
    return data.message;
  } catch (error) {
      console.error("[resetPassword] error: " + error);
      throw error;
  }
}

const changePassword = async (email: string, oldPassword: string, newPassword: string): Promise<string> => {
  try {
    const response = await fetch(
        `${getFunctionsBaseUrl()}/changePassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
        },
          body: JSON.stringify({
            email: email,
            oldPassword: oldPassword,
            newPassword: newPassword
          }),
        }
      );
    
    const data = await response.json();

    if (!response.ok) {
        const maybeError = data.error;
        // FirebaseError 객체로 throw
        if (isFirebaseError(maybeError)) {
          const firebaseError: FirebaseError = {
            code: maybeError.code,
            name: maybeError.name,
            message: maybeError.message
          };

          throw firebaseError;
        }

        // 그 외 일반 에러로 처리
        throw new Error(data.message || "Unknown error");
    }

    console.log(data);
    return data.message;
  } catch (error) {
      console.error("[resetPassword] error: " + error);
      throw error;
  }
}

const deleteAdminAccount = async (email: string): Promise<string> => {
  try {
    const response = await fetch(
        `${getFunctionsBaseUrl()}/deleteAdminAccount`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
        },
          body: JSON.stringify({
            email: email
          }),
        }
      );
    
    const data = await response.json();

    if (!response.ok) {
      const maybeError = data.error;
      // FirebaseError 객체로 throw
      if (isFirebaseError(maybeError)) {
        const firebaseError: FirebaseError = {
          code: maybeError.code,
          name: maybeError.name,
          message: maybeError.message
        };

        throw firebaseError;
      }

      // 그 외 일반 에러로 처리
      throw new Error(data.message || "Unknown error");
    }

    console.log(data);
    return data.message;
  } catch (error) {
      console.error("[deleteAdminAccount] error: " + error);
      throw error;
  }
}

export { verifyIdToken, sendAuthenticationMail, sendFindUsernameEmail, sendInquiryEmail, resetPassword, changePassword, deleteAdminAccount };